import {
  Autocomplete,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { type AwsRdsEngine } from '@repo/api-gw-sdk';

import { Icon } from '@/components/shared/icon';
import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

export const RdsInstanceClassSelect = ({
  accountId,
  regionName,
  engine,
  engineVersion,
  rdsInstanceClass,
  isCluster,
  onChange,
  isOptionalField,
  hideLabel,
  supportInherit = true,
}: {
  accountId?: string;
  regionName?: string;
  engine?: AwsRdsEngine;
  engineVersion?: string;
  rdsInstanceClass?: string;
  isCluster: boolean;
  onChange: (rdsInstanceClass?: string) => void;
  isOptionalField?: boolean;
  hideLabel?: boolean;
  supportInherit?: boolean;
}) => {
  const title = `DB instance class${isOptionalField ? ' (optional)' : ''}`;
  if (!accountId || !regionName || !engine || !engineVersion) {
    return <EmptySelect title={hideLabel ? '' : title} />;
  }

  return (
    <InnerRdsInstanceClassSelect
      accountId={accountId}
      regionName={regionName}
      engine={engine}
      engineVersion={engineVersion}
      onChange={onChange}
      rdsInstanceClass={rdsInstanceClass}
      isCluster={isCluster}
      title={title}
      hideLabel={hideLabel}
      supportInherit={supportInherit}
    />
  );
};

const InnerRdsInstanceClassSelect = ({
  accountId,
  regionName,
  engine,
  engineVersion,
  rdsInstanceClass,
  isCluster,
  onChange,
  title,
  hideLabel,
  supportInherit,
}: {
  accountId: string;
  regionName: string;
  engine: AwsRdsEngine;
  engineVersion: string;
  rdsInstanceClass?: string;
  isCluster: boolean;
  onChange: (rdsInstanceClass?: string) => void;
  title: string;
  hideLabel?: boolean;
  supportInherit: boolean;
}) => {
  const dal = useDAL();
  const { data: body } = dal.restore.rdsInstanceClasses.useList(
    accountId,
    engine,
    engineVersion,
    regionName
  );

  if (!body?.instanceClasses.length) {
    return <EmptySelect title={hideLabel ? '' : title} />;
  }

  const defaultSelectText = 'Inherit from the snapshot';
  const clusterTooltip =
    'Each restored instance in the cluster can inherit its original DB instance class from the snapshot, or you can override with a different instance class. If you override, all restored instances will have the same DB instance class.';
  const singleTooltip =
    'The restored instance can inherit the original DB instance class from the snapshot, or you can override with a different instance class.';

  const classes = supportInherit
    ? ['inherit', ...body.instanceClasses]
    : body.instanceClasses;

  return (
    <FormControl size='small' className='flex-1 group'>
      {!hideLabel && (
        <FormLabel>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
          >
            {title}
            {supportInherit && (
              <Tooltip title={isCluster ? clusterTooltip : singleTooltip}>
                <Icon iconClass='material-symbols-help-outline-rounded opacity-0 group-hover:opacity-100 transition-opacity' />
              </Tooltip>
            )}
          </Stack>
        </FormLabel>
      )}
      <Autocomplete
        clearOnBlur
        size='small'
        value={
          rdsInstanceClass === undefined
            ? supportInherit
              ? 'inherit'
              : ''
            : rdsInstanceClass
        }
        onChange={(event, newValue) =>
          onChange(newValue === 'inherit' ? undefined : newValue || undefined)
        }
        getOptionLabel={
          supportInherit
            ? (option) => (option !== 'inherit' ? option : defaultSelectText)
            : (option) => option
        }
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder='Select'
              label=''
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
        options={classes}
      />
    </FormControl>
  );
};
